import type { TFunction } from 'i18next';
import type { SCAPIShopperSearchSchemas } from '@/types';

type ProductSearchHit = SCAPIShopperSearchSchemas['ProductSearchHit'];

type EventTileCustomAttrs = {
    c_eventStartDate?: string | Date;
    c_eventVenueName?: string;
};

export type EventTileFields = {
    startDate: string | undefined;
    /**
     * Catalog/listing category label from `c_eventVenueName`
     * (venues like Torino Lingotto, or types like Cucina / Cena).
     */
    categoryName: string | undefined;
    isMaster: boolean;
};

function readTrimmedString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * Reads course/event tile metadata from a grouped or raw product search hit.
 * Prefers `representedProduct` custom attributes, then hit-level fallbacks.
 */
export function getEventTileFields(hit: ProductSearchHit): EventTileFields {
    const represented = hit.representedProduct as EventTileCustomAttrs | undefined;
    const hitLevel = hit as EventTileCustomAttrs;
    const productType = (hit as ProductSearchHit & { type?: { master?: boolean } }).type;

    const rawStartDate = represented?.c_eventStartDate ?? hitLevel.c_eventStartDate;

    const startDate =
        rawStartDate instanceof Date
            ? rawStartDate.toISOString()
            : typeof rawStartDate === 'string'
              ? rawStartDate
              : undefined;

    const categoryName =
        readTrimmedString(represented?.c_eventVenueName) ?? readTrimmedString(hitLevel.c_eventVenueName);

    return {
        startDate,
        categoryName,
        isMaster: hit.hitType === 'master' || productType?.master === true,
    };
}

/**
 * Formats an event start datetime for course/event product tiles (long localized date).
 * Example (it-IT): "9 febbraio 2026".
 */
export function formatEventStartDateForTile(isoDateTime: string | undefined, locale: string): string | undefined {
    if (!isoDateTime) return undefined;

    const date = new Date(isoDateTime);
    if (Number.isNaN(date.getTime())) return undefined;

    return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

/**
 * Formats an event start time for course/event overlay cards.
 * Example (it-IT): "18:00".
 */
export function formatEventStartTimeForTile(isoDateTime: string | undefined, locale: string): string | undefined {
    if (!isoDateTime) return undefined;

    const date = new Date(isoDateTime);
    if (Number.isNaN(date.getTime())) return undefined;

    return date.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Builds the date line for course/event tiles.
 * A master product may represent multiple sessions, so its date is rendered as "Dal {date}".
 */
export function formatEventDateLine({
    formattedDate,
    isMaster,
    t,
}: {
    formattedDate?: string;
    isMaster: boolean;
    t: TFunction<'search'>;
}): string | undefined {
    if (!formattedDate) {
        return undefined;
    }

    if (!isMaster) {
        return formattedDate;
    }

    return t('eventTile.fromDate', { date: formattedDate });
}
